import { useCallback, useContext, useMemo, useState } from 'react';
import UsersContext from 'contexts/UsersContext';
import AccountPickerContext from 'contexts/AccountPickerContext';
import userTypeCheckers from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/UsersProvider/userTypeCheckers';
import useLockedDebouncedEffect from 'hooks/useLockedDebouncedEffect';
import STORE from 'store';

export const useGetSharedWith = () => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { allUsers } = useContext(UsersContext);

  const getSharedWith = useCallback(
    async ({
      type,
      typeId,
    }: {
      type: 'report' | 'dashboard' | 'scorecard' | 'workSpace';
      typeId: string;
    }): Promise<
      (UserManagement.SignedUpUser | UserManagement.PendingUser)[]
    > => {
      const users = allUsers.filter(userTypeCheckers.isSignedUpOrPendingUser);
      const promises = users.map(async (user) => {
        const sharedDoc = await STORE.userManagement
          .getSharedContentRef({
            accountId: selectedAccountId,
            userId: user.id,
          })
          .get();

        const sharedContent = sharedDoc.data();
        if (!sharedContent) {
          return {
            user,
            isShared: false,
          };
        }

        const isShared = (() => {
          if (type === 'dashboard') {
            return sharedContent.dashboardIds.includes(typeId);
          } else if (type === 'report') {
            return sharedContent.reportIds.includes(typeId);
          } else if (type === 'scorecard') {
            return sharedContent.scorecardIds.includes(typeId);
          } else if (type === 'workSpace') {
            return sharedContent.workspaceIds.includes(typeId);
          } else {
            return false;
          }
        })();

        return {
          user,
          isShared,
        };
      });
      const results = await Promise.all(promises);
      const newSharedWith = results
        .filter((r) => r.isShared)
        .map((r) => r.user);
      return newSharedWith;
    },
    [allUsers, selectedAccountId],
  );

  return getSharedWith;
};

const useSharedWith = ({
  type,
  typeId,
}: {
  type: 'report' | 'dashboard' | 'scorecard' | 'workSpace';
  typeId: string;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sharedWith, setSharedWith] = useState<
    (UserManagement.SignedUpUser | UserManagement.PendingUser)[]
  >([]);
  const getSharedWith = useGetSharedWith();

  const responseHandler = useCallback(
    (
      newSharedWith: (
        | UserManagement.SignedUpUser
        | UserManagement.PendingUser
      )[],
    ) => {
      setSharedWith(newSharedWith);
      setIsLoading(false);
    },
    [],
  );

  const args = useMemo(() => {
    return { type, typeId };
  }, [type, typeId]);

  const callback = useCallback(
    ({
      type,
      typeId,
    }: {
      type: 'report' | 'dashboard' | 'scorecard' | 'workSpace';
      typeId: string;
    }) => {
      setIsLoading(true);
      return getSharedWith({ type, typeId });
    },
    [getSharedWith],
  );

  useLockedDebouncedEffect({
    callback,
    responseHandler,
    args,
  });

  const refreshSharedWith = useCallback(() => {
    setIsLoading(true);
    getSharedWith({ type, typeId }).then(responseHandler);
  }, [getSharedWith, responseHandler, type, typeId]);

  return {
    sharedWith,
    isLoadingSharedWith: isLoading,
    refreshSharedWith,
  };
};

export default useSharedWith;
